import { useDrag, UserHandlers } from "@use-gesture/react";
import classNames from "classnames";
import { action, runInAction, untracked } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { uiStateMobx } from "../SharedIframeState";
import { ActionType, PaneViewMode, Position, Rect } from "../Types";
import {
  areArraysEqual,
  doRectsOverlap,
  generatePaneId,
  getParentByFilter,
  isFirefox,
  isMacPlatform,
  throttle,
} from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import {
  activePaneStates,
  canvasAtom,
  CanvasTool,
  canvasTransformComputed,
  canvasViewport,
  canvasViewportShouldAnimateAtom,
  focusedPaneIdAtom,
  selectedPaneIdsAtom,
  selectedToolAtom,
} from "./CanvasState";
import { isCommandBarOpenBox } from "./CommandBar";
import { paneToolDragMobx, setFocusPaneIdOnMount } from "./NewPane";
import {
  addPositions,
  calculateNewZoomCanvasOffset,
  convertViewportPositionToCanvasOffset,
  EDIT_LAYOUT_CLASSNAME,
  getMinPaneWidth,
  mapClientPositionToCanvasPosition,
  OVERLAY_PANE_CLASSNAME,
  roundPosition,
  roundPositionToGrid,
  scalePosition,
  TEMPLATE_CANVAS_CLASSNAME,
  TOPBAR_CLASSNAME,
} from "./SandboxUtils";

const dragShield = document.createElement("div");
dragShield.className =
  "fixed top-0 left-0 right-0 bottom-0 z-99999 cursor-default";
const MAX_ZOOM_STEP = 10;

export const CanvasBackground = observer(
  ({
    setNewPaneMenuPosition,
  }: {
    setNewPaneMenuPosition: React.Dispatch<
      React.SetStateAction<Position | undefined>
    >;
  }) => {
    const cleanupRef = useRef<() => void>();
    const onCanvasContextMenu = useCallback((e: React.MouseEvent) => {
      setNewPaneMenuPosition(
        roundPositionToGrid(
          mapClientPositionToCanvasPosition([e.clientX, e.clientY]),
          1
        )
      );
      e.preventDefault();
    }, []);

    const [isWheeling, setIsWheeling] = useState(false);
    useEffect(() => {
      const throttleCancelMouseWheel = throttle(() => {
        setIsWheeling(false);
      }, 500);
      function onMouseWheel(e: WheelEvent) {
        runInAction(() => {
          setIsWheeling(true);
          if (e.ctrlKey || (isMacPlatform && e.metaKey)) {
            const newZoom =
              Math.ceil(
                Math.min(
                  Math.max(
                    canvasViewport.zoom -
                      Math.sign(e.deltaY) *
                        Math.min(Math.abs(e.deltaY) / 1000, MAX_ZOOM_STEP) *
                        canvasViewport.zoom,
                    0.1
                  ),
                  2
                ) * 100
              ) / 100;
            canvasViewport.offset = calculateNewZoomCanvasOffset(
              canvasViewport.offset,
              [e.clientX, e.clientY],
              canvasViewport.zoom,
              newZoom
            );
            canvasViewport.zoom = newZoom;
          } else {
            const transposeScroll = !isMacPlatform && e.shiftKey;
            if (!isFirefox) {
              canvasViewportShouldAnimateAtom.value = 30;
            }
            canvasViewport.offset = [
              canvasViewport.offset[0] +
                (transposeScroll ? e.deltaY : e.deltaX),
              canvasViewport.offset[1] +
                (transposeScroll ? e.deltaX : e.deltaY),
            ];
          }
          throttleCancelMouseWheel();
        });
      }
      function onBodyMouseWheel(e: WheelEvent) {
        if (
          uiStateMobx.maximizedPaneId !== undefined ||
          isCommandBarOpenBox.get()
        ) {
          return;
        }
        if (e.target !== null) {
          const invalidElement = getParentByFilter(
            e.target as HTMLElement,
            (element) =>
              element.classList.contains(EDIT_LAYOUT_CLASSNAME) ||
              element.classList.contains(TOPBAR_CLASSNAME) ||
              element.classList.contains(TEMPLATE_CANVAS_CLASSNAME) ||
              element.classList.contains(OVERLAY_PANE_CLASSNAME) ||
              ((element.scrollHeight > element.clientHeight ||
                element.scrollWidth > element.clientWidth) &&
                ["auto", "scroll"].includes(
                  window.getComputedStyle(element).overflowY
                ))
          );
          if (invalidElement === undefined) {
            onMouseWheel(e);
          }
        }
      }
      document.body.addEventListener("wheel", onBodyMouseWheel, {
        passive: false,
      });
      return () => {
        document.body.removeEventListener("wheel", onBodyMouseWheel);
      };
    }, []);

    const isPanning = selectedToolAtom.value[0] === "pan";
    const isAddingPane = selectedToolAtom.value[0] === "pane";
    useEffect(
      () => () => {
        if (cleanupRef.current !== undefined) {
          cleanupRef.current();
        }
      },
      []
    );

    const rootRef = useRef<HTMLDivElement | null>(null);
    const bindDrag = useDrag(
      action<UserHandlers["onDrag"]>(
        ({
          event,
          shiftKey,
          target,
          first,
          down,
          last,
          initial,
          xy,
          touches,
          cancel,
          memo = {},
        }) => {
          if (touches > 1) {
            cancel();
            return;
          }
          if (first) {
            focusedPaneIdAtom.value = undefined;
          }
          let canvasTool: CanvasTool | undefined = memo.canvasTool;
          if (canvasTool === undefined) {
            canvasTool =
              // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/buttons#value
              (event as any).buttons === 4
                ? ["pan", undefined]
                : selectedToolAtom.value;
            if (
              canvasTool[0] === "pointer" &&
              (event as any).pointerType === "touch"
            ) {
              canvasTool = ["pan", undefined];
            }
            memo.canvasTool = canvasTool;
          }
          switch (canvasTool[0]) {
            case "pan": {
              if (first) {
                memo.initialOffset = canvasViewport.offset;
                if (target instanceof HTMLElement) {
                  target.style.cursor = "grabbing";
                }
              } else if (last) {
                if (target instanceof HTMLElement) {
                  target.style.cursor = "";
                }
              } else if (down) {
                const { initialOffset } = memo;
                canvasViewport.offset = roundPosition(
                  addPositions(
                    initialOffset,
                    scalePosition(
                      [initial[0] - xy[0], initial[1] - xy[1]],
                      1 / canvasViewport.zoom
                    )
                  )
                );
              }
              break;
            }
            case "pane": {
              const paneType = canvasTool[1];
              if (first) {
                memo.position = xy;
              } else if (last) {
                const { position, size } = memo;
                if (size === undefined || (size[0] >= 64 && size[1] >= 64)) {
                  const paneId = generatePaneId();
                  setFocusPaneIdOnMount(paneId);
                  canvasDispatch([
                    ActionType.CreatePane,
                    {
                      paneId,
                      type: paneType,
                      position,
                      size:
                        size !== undefined
                          ? [
                              Math.max(size[0], getMinPaneWidth(paneType)),
                              Math.max(size[1], 96),
                            ]
                          : undefined,
                    },
                  ]);
                }
                paneToolDragMobx.set(undefined);
                selectedToolAtom.value = ["pointer"];
              } else {
                const size = addPositions(xy, [
                  -memo.position[0],
                  -memo.position[1],
                ]);
                memo.size = size;
                const paneToolDrag = paneToolDragMobx.get();
                if (paneToolDrag === undefined) {
                  paneToolDragMobx.set({
                    paneType,
                    position: memo.position,
                    size,
                  });
                } else {
                  paneToolDrag.size = size;
                }
              }
              break;
            }
            case "pointer": {
              if (first) {
                const previousSelectedPaneIds = shiftKey
                  ? selectedPaneIdsAtom.value
                  : [];
                memo.previousSelectedPaneIds = previousSelectedPaneIds;
                rootRef.current!.appendChild(dragShield);
              } else if (last) {
                const { dragRectangleElement } = memo;
                if (dragRectangleElement !== undefined) {
                  document.body.removeChild(dragRectangleElement);
                } else {
                  selectedPaneIdsAtom.value = [];
                }
                rootRef.current?.removeChild(dragShield);
              } else {
                let { dragRectangleElement, previousSelectedPaneIds } = memo;
                if (dragRectangleElement === undefined) {
                  dragRectangleElement = document.createElement("div");
                  dragRectangleElement.className =
                    "absolute border border-primary-400 bg-opacity-25 bg-primary-100 rounded-sm z-99999 pointer-events-none";
                  document.body.append(dragRectangleElement);
                  memo.dragRectangleElement = dragRectangleElement;
                }
                const [minX, minY] = [
                  Math.min(initial[0], xy[0]),
                  Math.min(initial[1], xy[1]),
                ];
                const [maxX, maxY] = [
                  Math.max(initial[0], xy[0]),
                  Math.max(initial[1], xy[1]),
                ];
                const rect: Rect = [minX, minY, maxX - minX, maxY - minY];
                dragRectangleElement.style.left = `${rect[0]}px`;
                dragRectangleElement.style.top = `${rect[1]}px`;
                dragRectangleElement.style.width = `${rect[2]}px`;
                dragRectangleElement.style.height = `${rect[3]}px`;

                const transformedRectPosition = scalePosition(
                  addPositions(
                    [minX, minY],
                    canvasTransformComputed.get().map((v) => -v) as Position
                  ),
                  1 / canvasViewport.zoom
                );
                const transformedRect: Rect = [
                  transformedRectPosition[0],
                  transformedRectPosition[1],
                  rect[2] / canvasViewport.zoom,
                  rect[3] / canvasViewport.zoom,
                ];

                const panes = canvasAtom.value.panes;
                const overlappingPaneIds = Array.from(
                  panes.map((pane) => pane.id)
                ).filter(
                  (paneId) =>
                    activePaneStates.get(paneId)!.viewMode.get() !==
                      PaneViewMode.Hidden &&
                    doRectsOverlap(
                      activePaneStates.get(paneId)!.rect.get(),
                      transformedRect
                    )
                );
                const newSelectedPaneIdSet: Set<string> = new Set(
                  previousSelectedPaneIds
                );
                overlappingPaneIds.forEach((paneId) => {
                  if (newSelectedPaneIdSet.has(paneId)) {
                    newSelectedPaneIdSet.delete(paneId);
                  } else {
                    newSelectedPaneIdSet.add(paneId);
                  }
                });
                const newSelectedPaneIds = Array.from(newSelectedPaneIdSet);
                if (
                  !areArraysEqual(selectedPaneIdsAtom.value, newSelectedPaneIds)
                ) {
                  selectedPaneIdsAtom.value = newSelectedPaneIds;
                }
              }
              break;
            }
          }
          return memo;
        }
      ),
      {
        transform: ([x, y]) => {
          if (selectedToolAtom.value[0] === "pane") {
            return untracked(() =>
              roundPositionToGrid(
                convertViewportPositionToCanvasOffset(
                  [x, y],
                  canvasTransformComputed.get(),
                  canvasViewport.zoom
                ),
                1,
                Math.round
              )
            );
          }
          return [x, y];
        },
        pointer: {
          buttons: [1, 4],
        },
      }
    );
    useEffect(() => {
      function onGestureEvent(e: Event) {
        e.preventDefault();
      }
      document.addEventListener("gesturestart", onGestureEvent);
      document.addEventListener("gesturechange", onGestureEvent);
      return () => {
        document.removeEventListener("gesturestart", onGestureEvent);
        document.removeEventListener("gesturechange", onGestureEvent);
      };
    }, []);

    return (
      <div
        {...bindDrag()}
        className={classNames(
          "absolute top-20 md:top-12 left-0 bottom-0 right-0 touch-none",
          {
            "cursor-grab": isPanning,
            "cursor-crosshair": isAddingPane,
            "z-99999": isPanning || isWheeling,
          }
        )}
        onContextMenu={onCanvasContextMenu}
        ref={rootRef}
      ></div>
    );
  }
);
